import { useEffect, useLayoutEffect, useState, type FormEvent } from 'react';
import { CheckCircle2, LoaderCircle, ArrowRight } from 'lucide-react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { acceptInvite, completeOrganisationOnboarding } from '../lib/api';
import { SiteHeader } from './MarketingSite';

type AuthMode = 'signin' | 'signup' | 'invite_signup' | 'forgot' | 'reset';

export function isPasswordSetupRoute(search: string): boolean {
  return new URLSearchParams(search).get('mode') === 'reset';
}

export function shouldKeepPasswordSetup(event: string, search: string): boolean {
  return event === 'PASSWORD_RECOVERY' || isPasswordSetupRoute(search);
}

function AuthMetadata() {
  useLayoutEffect(() => {
    document.title = 'Sign in | WellDone';
    const upsert = (selector: string, attribute: 'name' | 'property', key: string, value: string) => {
      let element = document.head.querySelector<HTMLMetaElement>(selector);
      if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, key); document.head.appendChild(element); }
      element.content = value;
    };
    upsert('meta[name="description"]', 'name', 'description', 'Sign in to your WellDone exploration workspace.');
    upsert('meta[name="robots"]', 'name', 'robots', 'noindex, nofollow');
    upsert('meta[property="og:title"]', 'property', 'og:title', 'Sign in | WellDone');
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.welldonebud.com/sign-in';
  }, []);
  return null;
}

export function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { demoMode } = useAuth();
  const selectedPlan = new URLSearchParams(location.search).get('plan') === 'pro' ? 'pro' : new URLSearchParams(location.search).get('plan') === 'standard' ? 'standard' : null;
  const inviteToken = new URLSearchParams(location.search).get('invite_token');
  const postAuthRoute = inviteToken ? `/accept-invite?token=${encodeURIComponent(inviteToken)}` : selectedPlan ? `/app/team?plan=${selectedPlan}` : '/app';
  const shouldCreateOrganisation = location.hash === '#create-organisation';
  const passwordSetupRoute = isPasswordSetupRoute(location.search);
  const [mode, setMode] = useState<AuthMode>(() => passwordSetupRoute ? 'reset' : inviteToken ? 'invite_signup' : shouldCreateOrganisation ? 'signup' : 'signin');

  useEffect(() => {
    if (passwordSetupRoute) setMode('reset');
    else if (inviteToken) setMode('invite_signup');
    else if (location.hash === '#create-organisation') setMode('signup');
  }, [inviteToken, location.hash, passwordSetupRoute]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!supabase) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Supabase can emit SIGNED_IN or INITIAL_SESSION before PASSWORD_RECOVERY.
      // The reset route is authoritative: never route a recovery or invitation
      // password-setup session into the workspace until updateUser succeeds.
      if (shouldKeepPasswordSetup(event, location.search)) {
        setMode('reset');
        setError('');
        setMessage('Choose a new password for your WellDone account.');
        return;
      }
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        // Defer work until the auth listener releases its lock, then finish any
        // email-confirmed self-service organisation setup through the protected API.
        window.setTimeout(() => {
          void completeOrganisationOnboarding().then(() => {
            navigate(postAuthRoute, { replace: true });
          }).catch((reason) => {
            setError(reason instanceof Error ? reason.message : 'Your account is confirmed, but we could not finish setting up your organisation. Please try signing in again.');
          });
        }, 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [location.search, navigate, passwordSetupRoute, postAuthRoute]);

  const changeMode = (next: AuthMode) => {
    setMode(next);
    setError('');
    setMessage('');
    setPassword('');
    setConfirmPassword('');
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    if (demoMode) { navigate('/app'); return; }
    if (!supabase) return;
    setLoading(true);
    try {
      if (mode === 'forgot') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/sign-in?mode=reset` });
        if (resetError) throw resetError;
        setMessage('If an account exists for this email, we have sent a one-time password-reset link. Check your inbox and use the link once.');
      } else if (mode === 'reset') {
        if (password !== confirmPassword) throw new Error('The passwords do not match.');
        const { data: recoverySession, error: recoverySessionError } = await supabase.auth.getSession();
        if (recoverySessionError || !recoverySession.session) {
          throw new Error('This password-reset link is invalid or has expired. Request a new reset link and use it once.');
        }
        const recoveryEmail = recoverySession.session.user.email;
        if (!recoveryEmail) {
          throw new Error('We could not verify the account for this reset link. Request a new reset link and use it once.');
        }
        const { error: updateError } = await supabase.auth.updateUser({ password });
        if (updateError) throw updateError;
        setEmail(recoveryEmail);
        setPassword('');
        setConfirmPassword('');
        await completeOrganisationOnboarding();
        navigate(postAuthRoute, { replace: true });
      } else if (mode === 'signin') {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) {
          setError('We could not sign you in. Check your email address and password, then try again. If you have forgotten your password, reset it securely below.');
          return;
        }
        // The SIGNED_IN listener completes any pending organisation setup before routing.
        setMessage('Signing you in…');
      } else if (mode === 'signup') {
        if (!tenantName.trim() || !displayName.trim()) throw new Error('Enter your name and organisation name.');
        const slug = tenantName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const { data, error: signUpError } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: `${window.location.origin}/sign-in`, data: { display_name: displayName.trim(), tenant_name: tenantName.trim(), tenant_slug: slug } } });
        if (signUpError) throw signUpError;
        if (data.session) {
          // The SIGNED_IN listener completes any pending organisation setup before routing.
          setMessage('Signing you in…');
        } else {
          setMessage('Check your inbox to confirm your email, then return to finish creating your organisation.');
        }
      } else {
        if (!displayName.trim() || !inviteToken) throw new Error('This invitation link is incomplete. Ask a tenant administrator to send a new invitation.');
        const inviteRedirect = `${window.location.origin}/sign-in?invite_token=${encodeURIComponent(inviteToken)}`;
        const { data, error: signUpError } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: inviteRedirect, data: { display_name: displayName.trim() } } });
        if (signUpError) throw signUpError;
        if (data.session) {
          // The SIGNED_IN listener preserves the invitation and returns to explicit acceptance.
          setMessage('Signing you in…');
        } else {
          setMessage('Check your inbox to confirm your email, then return to accept this invitation.');
        }
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Authentication failed.');
    } finally {
      setLoading(false);
    }
  };

  const leavePasswordSetup = () => {
    if (passwordSetupRoute) navigate('/sign-in', { replace: true });
    changeMode('signin');
  };

  const title = mode === 'signup' ? 'Start with your team' : mode === 'invite_signup' ? 'Create your invited account' : mode === 'forgot' ? 'Reset your password' : mode === 'reset' ? 'Choose a new password' : 'Welcome back';
  const copy = demoMode ? 'No backend is configured, so this routes to the seeded product demo.' : mode === 'signup' ? 'Create a tenant administrator account for your organisation.' : mode === 'invite_signup' ? 'Create an account using the invited email address, then explicitly accept your team invitation.' : mode === 'forgot' ? 'Enter your work email and we will send a secure, one-time reset link.' : mode === 'reset' ? 'Set a new password for your WellDone workspace.' : inviteToken ? 'Sign in with the invited email address to continue to invitation acceptance.' : 'Sign in to your exploration workspace.';
  const submitLabel = demoMode ? 'Open seeded demo' : mode === 'signup' ? 'Create organisation' : mode === 'invite_signup' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : mode === 'reset' ? 'Save new password' : 'Sign in';

  return <><AuthMetadata /><div className="auth-page auth-page--public-nav"><a className="skip-link" href="#main-content">Skip to content</a><SiteHeader /><main id="main-content"><section className="auth-intro"><span className="eyebrow">Exploration intelligence</span><h1>Make the next log <em>count.</em></h1><p>Run an auditable first-pass interpretation while keeping every organisation’s exploration data isolated.</p><div className="auth-benefits"><span><CheckCircle2 size={16} />Deterministic petrophysics</span><span><CheckCircle2 size={16} />Tenant-isolated data</span><span><CheckCircle2 size={16} />Human feedback loop</span></div></section><section className="auth-card" id="create-organisation">{mode === 'forgot' || mode === 'reset' || mode === 'invite_signup' ? <button type="button" className="auth-back" onClick={leavePasswordSetup}>← Back to sign in</button> : <div className="auth-tabs"><button type="button" className={mode === 'signin' ? 'active' : ''} onClick={() => changeMode('signin')}>Sign in</button><button type="button" className={mode === 'signup' ? 'active' : ''} onClick={() => changeMode('signup')}>Create organisation</button></div>}<div className="auth-card-heading"><h2>{title}</h2><p>{copy}</p></div><form onSubmit={(event) => void submit(event)}>{(mode === 'signup' || mode === 'invite_signup') && <label>Your name<input required value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Jordan Tan" /></label>}{mode === 'signup' && <label>Organisation name<input required value={tenantName} onChange={(event) => setTenantName(event.target.value)} placeholder="Frome South Exploration" /></label>}{mode !== 'reset' && <label>Work email<input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@company.com" /></label>}{mode !== 'forgot' && <label>{mode === 'reset' ? 'New password' : 'Password'}<input required type="password" minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="At least 8 characters" autoComplete={mode === 'reset' || mode === 'signup' || mode === 'invite_signup' ? 'new-password' : 'current-password'} /></label>}{mode === 'reset' && <label>Confirm new password<input required type="password" minLength={8} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Repeat your new password" autoComplete="new-password" /></label>}{error && <div className="form-error auth-error-guidance" role="alert"><span>{error}</span>{mode === 'signin' && !demoMode && <button type="button" onClick={() => changeMode('forgot')}>Reset password</button>}</div>}{message && <div className="auth-message" role="status"><CheckCircle2 size={17} />{message}</div>}{mode === 'signin' && !demoMode && <><button type="button" className="auth-recovery-link" onClick={() => changeMode('forgot')}>Forgot your password? Reset it securely</button>{inviteToken && <button type="button" className="auth-recovery-link" onClick={() => changeMode('invite_signup')}>Create an account using the invited email</button>}</>}<button className="button auth-submit" disabled={loading}>{loading && <LoaderCircle className="spin" size={16} />}{submitLabel}<ArrowRight size={16} /></button></form></section></main></div></>;
}

export function ConfirmInvitationEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const tokenHash = searchParams.get('token_hash');

  const continueToAccountSetup = async () => {
    if (!supabase || !tokenHash) {
      setError('This invitation link is incomplete. Ask a WellDone administrator to send a new invitation.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: verificationError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'invite' });
      if (verificationError) {
        throw new Error('This invitation link is invalid or has expired. Ask a WellDone administrator to send a new invitation.');
      }
      navigate('/sign-in?mode=reset', { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'We could not verify this invitation. Ask a WellDone administrator to send a new invitation.');
    } finally {
      setLoading(false);
    }
  };

  return <><AuthMetadata /><div className="auth-page auth-page--public-nav"><a className="skip-link" href="#main-content">Skip to content</a><SiteHeader /><main id="main-content"><section className="auth-intro"><span className="eyebrow">WellDone account invitation</span><h1>Set up your <em>workspace access.</em></h1><p>You have been invited to create a WellDone account. Continue only if you expected this invitation.</p></section><section className="auth-card"><div className="auth-card-heading"><h2>Accept invitation</h2><p>For your security, the invitation is activated only after you choose to continue.</p></div>{error && <div className="form-error" role="alert">{error}</div>}<button className="button auth-submit" onClick={() => void continueToAccountSetup()} disabled={loading}>{loading ? 'Verifying invitation…' : 'Continue to account setup'}<ArrowRight size={16} /></button><button className="auth-recovery-link" type="button" onClick={() => navigate('/sign-in')}>Back to sign in</button></section></main></div></>;
}

export const INVITATION_RECIPIENT_GUIDANCE = 'This invitation is invalid, expired, or belongs to a different signed-in account. Sign in using the invited email address, then reopen the invitation.';

export function invitationAcceptanceFailureMessage(reason: unknown): string {
  const message = reason instanceof Error ? reason.message : 'Unable to accept invitation.';
  return message === 'Invitation is invalid, expired, or was sent to a different email address.' ? INVITATION_RECIPIENT_GUIDANCE : message;
}

export function requiresInvitedAccountSignIn(message: string): boolean {
  return message === INVITATION_RECIPIENT_GUIDANCE;
}

export function inviteAccountSignInRoute(rawToken: string): string {
  return `/sign-in?invite_token=${encodeURIComponent(rawToken)}`;
}

export function AcceptInvite() {
  const { session, demoMode, refreshAccess, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const rawToken = new URLSearchParams(location.search).get('token');
  const [error, setError] = useState('');
  const [accountMismatch, setAccountMismatch] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const accept = async () => {
    if (demoMode) { navigate('/app'); return; }
    if (!rawToken || !session || !supabase) return;
    setLoading(true); setError(''); setAccountMismatch(false);
    try {
      await acceptInvite(rawToken);
      await refreshAccess();
      setDone(true);
    } catch (reason) {
      const message = invitationAcceptanceFailureMessage(reason);
      setError(message);
      setAccountMismatch(requiresInvitedAccountSignIn(message));
    }
    finally { setLoading(false); }
  };
  const signInWithInvitedAccount = async () => {
    if (!rawToken) return;
    setLoading(true); setError('');
    try {
      await signOut();
      navigate(inviteAccountSignInRoute(rawToken), { replace: true });
    } catch {
      setError('We could not sign out of the current account. Close this tab, sign out, then open the invitation email again.');
    } finally { setLoading(false); }
  };
  return <div className="auth-page auth-page--public-nav invite-page"><a className="skip-link" href="#main-content">Skip to content</a><SiteHeader /><main id="main-content"><section className="auth-card"><div className="auth-card-heading"><span className="eyebrow">Team invitation</span><h2>{done ? 'You are on the team' : 'Join WellDone'}</h2><p>{done ? 'Your tenant membership has been added. Continue to your workspace.' : !rawToken ? 'This invitation link is incomplete.' : !session ? 'Sign in or create an account using the invited email, then reopen this link.' : 'Accept this invitation to access the organisation’s exploration workspace.'}</p></div>{error && <div className="form-error invite-error" role="alert"><strong>{accountMismatch ? 'This invitation needs a different account' : 'We could not accept this invitation'}</strong><span>{error}</span></div>}{accountMismatch && session && rawToken && <section className="invite-account-recovery" aria-label="Invitation account recovery"><h3>Continue with the invited email</h3><p>You are signed in with a different WellDone account. We will safely sign out of this account so you can sign in or create the account that received the invitation.</p><button type="button" className="button auth-submit invite-account-recovery__action" onClick={() => void signInWithInvitedAccount()} disabled={loading}>Sign in with the invited email<ArrowRight size={16} /></button></section>}{done ? <button className="button auth-submit" onClick={() => navigate('/app')}>Open workspace <ArrowRight size={16} /></button> : session && rawToken ? <button className="button auth-submit" disabled={loading} onClick={() => void accept()}>{loading ? 'Joining…' : 'Accept invitation'}<ArrowRight size={16} /></button> : <NavLink className="button auth-submit" to={rawToken ? inviteAccountSignInRoute(rawToken) : '/sign-in'}>Sign in or create account <ArrowRight size={16} /></NavLink>}</section></main></div>;
}
